import React, { forwardRef, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import HitchhikerDialog from "./HitchhikerDialog";

import qrCodeImage from "../assets/QRCodeImage.png";

const ScrollerContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: visible;
  scrollbar-width: none;
  -ms-overflow-style: none;
  pointer-events: all;
  z-index: 1;
  background: transparent;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const NumberWrapper = styled.div`
  padding: 40px 0px;
  margin-top: 0;
  padding-left: 0;
  padding-right: 0;
  width: 100%;
  flex-grow: 1;
  min-height: 100vh;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-x: visible;
  @media (max-width: 1100px) {
    padding: 20px 0;
  }
  @media (max-width: 900px) {
    padding: 10px 0;
  }
`;

const ContentOverlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 65%; // Increase width to match
  height: 100vh;
  pointer-events: none;
  padding-left: 6rem; // Add left padding for spacing
`;

const NumberItem = styled(motion.div)`
  pointer-events: all;
  font-size: clamp(3rem, 8vw + 2rem, 14rem);
  font-weight: 700;
  color: ${props => (props.active ? "#FFFFFF" : "rgba(255, 255, 255, 0.15)")};
  text-shadow: ${props =>
    props.active ? "2px 0 #00FF00, 0 2px #00FF00" : "none"};
  cursor: pointer;
  transition:
    color 0.3s ease,
    text-shadow 0.3s ease,
    transform 0.3s ease;
  padding: 0.5rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  font-family: "Chakra Petch", sans-serif;
  letter-spacing: -0.05em;
  transform: ${props => (props.active ? "scale(1)" : "scale(1)")}
    scale(clamp(0.4, 0.3 + 0.7 * (100vw - 900px) / (1430px - 900px), 1));
  overflow: visible;

  @media (max-width: 1430px) {
    transform: ${props => (props.active ? "scale(1)" : "scale(1)")}
      scale(clamp(0.4, 0.3 + 0.7 * (100vw - 900px) / (1430px - 900px), 1));
  }

  @media (max-width: 900px) {
    transform: ${props => (props.active ? "scale(1)" : "scale(1)")} scale(0.4);
  }
`;

const NoText = styled.div`
  font-size: clamp(1rem, 2vw + 0.5rem, 3.75rem);
  font-weight: 900;
  color: ${props => (props.active ? "#FFFFFF" : "rgba(255, 255, 255, 0.15)")};
  font-family: "Inter", sans-serif;
  position: static;

  left: clamp(-1rem, -2vw - 0.5rem, -4rem);
  top: 50%;
  transform: translateY(-90%);
  pointer-events: none;
  transition: color 0.3s ease;
  text-shadow: none;
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: visible;
`;

const NumberContent = styled.div`
  position: relative;
  display: inline-block;
`;

const CircleWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -40%);
  min-width: clamp(120px, 15vw + 80px, 300px);
  height: auto;
  pointer-events: none;
  opacity: ${props => (props.active ? 1 : 0)};
  transition: opacity 0.3s ease;
`;

const AnimatedCircle = styled(motion.svg)`
  width: 100%;
  height: 100%;
`;

const CirclePath = styled(motion.path)`
  fill: none;
  stroke: #00ff00;
  stroke-width: 8;
  stroke-linecap: round;
`;

// Add new styled component for the button container
const ButtonContainer = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  display: flex;
  align-items: stretch;
  gap: 8px;
  z-index: 10;
`;

// Update the QRButton styling
const QRButton = styled.button`
  background: white;
  border: none;
  width: 40px;
  padding: 0.4rem;
  border-radius: 12px; // Updated from 16px to 12px
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f0f0f0;
  }

  img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
`;

// Update ShuffleButton with increased border radius
const ShuffleButton = styled.button`
  background: white;
  color: #0a0a1a;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 12px; // Updated from 16px to 12px
  font-weight: 900;
  font-family: "Inter", sans-serif;
  font-style: italic;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 0.9rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  &:hover {
    background: #f0f0f0;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

// Add QuestionButton styled component
const QuestionButton = styled.button`
  background: white;
  border: none;
  padding: 0.4rem;
  border-radius: 12px;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: #0a0a1a;
  font-weight: bold;
  width: 40px;
  min-height: 100%;

  &:hover {
    background: #f0f0f0;
  }
`;

const NumberScroller = forwardRef(
  ({ currentNumber, onNumberChange, onOpenQRDialog }, ref) => {
    const containerRef = useRef(null);
    const [isShuffling, setIsShuffling] = useState(false);
    const [isNavigating, setIsNavigating] = useState(false);
    const lastNumberRef = useRef(currentNumber);
    const navigationTimeoutRef = useRef(null);
    const [isHitchhikerDialogOpen, setIsHitchhikerDialogOpen] = useState(false);

    // Add flag to prevent scroll detection during initial load
    const [, setIsInitialLoad] = useState(true);
    const [allowScrollDetection, setAllowScrollDetection] = useState(false);

    // Two-array system for circular scrolling
    const ascendingArray = Array.from({ length: 42 }, (_, i) => i + 1); // [1, 2, 3, ..., 42]

    // Create infinite circular array: ...1,2,3...42,1,2,3...42,1,2,3...42
    // We'll create three cycles to ensure smooth scrolling in both directions
    const createInfiniteArray = () => {
      const singleCycle = [...ascendingArray]; // [1,2,3...42]
      return [...singleCycle, ...singleCycle, ...singleCycle]; // Three full cycles
    };

    const infiniteArray = createInfiniteArray();
    const CYCLE_LENGTH = 42;
    const MIDDLE_CYCLE_START = CYCLE_LENGTH; // Start position of middle cycle

    // Track programmatic scrolls and debounce lock
    const isProgrammaticScroll = useRef(false);
    const scrollDetectionLock = useRef(false);

    // Manual scroll positioning function for precise centering
    const scrollToElementCenter = (element, behavior = "smooth") => {
      if (!element || !containerRef.current) return;

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      // Calculate the current scroll position
      const currentScrollTop = container.scrollTop;

      // Calculate where the element currently is relative to the container's top
      const elementTopRelativeToContainer =
        elementRect.top - containerRect.top + currentScrollTop;

      // Calculate the element's visual center (accounting for its height)
      const elementHeight = elementRect.height;
      const elementCenter = elementTopRelativeToContainer + elementHeight / 2;

      // Calculate where we want the element center to be (container's visual center)
      const containerCenter = containerRect.height / 2;

      // Calculate the exact scroll position needed
      const targetScrollTop = elementCenter - containerCenter;

      // Perform the scroll
      container.scrollTo({
        top: targetScrollTop,
        behavior: behavior,
      });
    };

    // Get scroll position for a specific number (choose shortest path)
    const getScrollPositionForNumber = (targetNumber, fromNumber = null) => {
      if (!fromNumber) {
        // Default to middle cycle when no source is specified
        return MIDDLE_CYCLE_START + (targetNumber - 1);
      }

      // Find current position in the infinite array
      const currentElements = Array.from(
        containerRef.current?.querySelectorAll(
          `[data-number="${fromNumber}"]`
        ) || []
      );
      let currentPosition = MIDDLE_CYCLE_START + (fromNumber - 1); // Default fallback

      if (currentElements.length > 0) {
        // Find which instance is currently visible (closest to center)
        const containerRect = containerRef.current?.getBoundingClientRect();
        if (containerRect) {
          const containerCenter = containerRect.top + containerRect.height / 2;
          let closestDistance = Infinity;

          currentElements.forEach(element => {
            const elementRect = element.getBoundingClientRect();
            const elementCenter = elementRect.top + elementRect.height / 2;
            const distance = Math.abs(elementCenter - containerCenter);

            if (distance < closestDistance) {
              closestDistance = distance;
              currentPosition = parseInt(
                element.getAttribute("data-position"),
                10
              );
            }
          });
        }
      }

      // Calculate shortest path to target
      const targetPositions = [
        targetNumber - 1, // First cycle
        CYCLE_LENGTH + (targetNumber - 1), // Second cycle
        CYCLE_LENGTH * 2 + (targetNumber - 1), // Third cycle
      ];

      // Find the target position with shortest distance
      let shortestDistance = Infinity;
      let bestTargetPosition = targetPositions[1]; // Default to middle cycle

      targetPositions.forEach(pos => {
        const distance = Math.abs(pos - currentPosition);
        if (distance < shortestDistance) {
          shortestDistance = distance;
          bestTargetPosition = pos;
        }
      });

      return bestTargetPosition;
    };

    // Initialize the component properly for title screen
    useEffect(() => {
      if (currentNumber === 0) {
        setIsInitialLoad(true);
        setAllowScrollDetection(false);
        // Position scroll at the beginning of middle cycle for title screen
        setTimeout(() => {
          if (containerRef.current) {
            const middleCycleStart = containerRef.current.querySelector(
              `[data-position="${MIDDLE_CYCLE_START}"]`
            );
            if (middleCycleStart) {
              middleCycleStart.scrollIntoView({
                behavior: "auto",
                block: "center",
              });
            }
          }
        }, 100);
      } else {
        // When transitioning away from title screen, enable scroll detection
        setIsInitialLoad(false);
        // Navigate to the proper position for the current number
        if (currentNumber > 0) {
          // Use immediate positioning for first navigation
          const targetPosition = getScrollPositionForNumber(currentNumber);
          setTimeout(() => {
            if (containerRef.current) {
              const element = containerRef.current.querySelector(
                `[data-position="${targetPosition}"]`
              );
              if (element) {
                element.scrollIntoView({ behavior: "auto", block: "center" });
              }
            }
            setAllowScrollDetection(true);
          }, 200);
        } else {
          setAllowScrollDetection(true);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentNumber]);

    // Unified navigation function
    const navigateToNumber = (targetNumber, behavior = "smooth") => {
      if (isShuffling || targetNumber === 0) return;

      // Don't block if already navigating - just update the target
      if (isNavigating) {
        if (navigationTimeoutRef.current) {
          clearTimeout(navigationTimeoutRef.current);
        }
      }

      setIsNavigating(true);

      // Update the state immediately for instant visual feedback
      lastNumberRef.current = targetNumber;
      onNumberChange(targetNumber);

      // Find the appropriate element to scroll to using shortest path
      const scrollToTarget = () => {
        if (containerRef.current) {
          const targetPosition = getScrollPositionForNumber(
            targetNumber,
            currentNumber
          );
          const element = containerRef.current.querySelector(
            `[data-position="${targetPosition}"]`
          );
          if (element) {
            isProgrammaticScroll.current = true;
            scrollDetectionLock.current = true;
            // Replace scrollIntoView with manual positioning
            scrollToElementCenter(element, behavior);
            setTimeout(
              () => {
                isProgrammaticScroll.current = false;
                // Keep lock for a bit longer to allow scroll to settle
                setTimeout(() => {
                  scrollDetectionLock.current = false;
                }, 200);
              },
              behavior === "smooth" ? 500 : 50
            );
          }
        }
      };

      // Immediate scroll without delay for better responsiveness
      scrollToTarget();

      // Re-enable navigation after scroll animation completes
      const scrollDuration = behavior === "smooth" ? 500 : 50; // Reduced duration
      navigationTimeoutRef.current = setTimeout(() => {
        setIsNavigating(false);
      }, scrollDuration);
    };

    // Simplified keyboard navigation without complex array switching
    useEffect(() => {
      const handleKeyDown = e => {
        if (isShuffling) return;
        if (e.key === "ArrowUp") {
          e.preventDefault();
          if (currentNumber === 0) {
            navigateToNumber(1);
            return;
          }
          const prevNumber = currentNumber === 1 ? 42 : currentNumber - 1;
          navigateToNumber(prevNumber);
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          if (currentNumber === 0) {
            navigateToNumber(1);
            return;
          }
          const nextNumber = currentNumber === 42 ? 1 : currentNumber + 1;
          navigateToNumber(nextNumber);
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentNumber, isShuffling]);

    // Add simplified scroll detection with debounce/lock and window-level wheel handling
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;
      let debounceTimer = null;
      const handleScroll = () => {
        if (isShuffling || !allowScrollDetection || currentNumber === 0) {
          return;
        }
        if (isProgrammaticScroll.current || scrollDetectionLock.current) {
          return;
        }

        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          const containerRect = container.getBoundingClientRect();
          const containerCenter = containerRect.top + containerRect.height / 2;
          let closestElement = null;
          let closestDistance = Infinity;
          container.querySelectorAll("[data-number]").forEach(element => {
            const elementRect = element.getBoundingClientRect();
            const elementCenter = elementRect.top + elementRect.height / 2;
            const distance = Math.abs(elementCenter - containerCenter);
            if (distance < closestDistance) {
              closestDistance = distance;
              closestElement = element;
            }
          });
          if (closestElement) {
            const number = parseInt(
              closestElement.getAttribute("data-number"),
              10
            );
            const position = parseInt(
              closestElement.getAttribute("data-position"),
              10
            );
            // Handle infinite scroll wrap-around with reduced threshold
            if (position < CYCLE_LENGTH * 0.25) {
              const newPosition = getScrollPositionForNumber(number);
              const newElement = container.querySelector(
                `[data-position="${newPosition}"]`
              );
              if (newElement && !isNavigating) {
                isProgrammaticScroll.current = true;
                scrollDetectionLock.current = true;
                newElement.scrollIntoView({
                  behavior: "auto",
                  block: "center",
                });
                setTimeout(() => {
                  isProgrammaticScroll.current = false;
                  setTimeout(() => {
                    scrollDetectionLock.current = false;
                  }, 200);
                }, 50);
              }
            } else if (position >= CYCLE_LENGTH * 2.75) {
              const newPosition = getScrollPositionForNumber(number);
              const newElement = container.querySelector(
                `[data-position="${newPosition}"]`
              );
              if (newElement && !isNavigating) {
                isProgrammaticScroll.current = true;
                scrollDetectionLock.current = true;
                newElement.scrollIntoView({
                  behavior: "auto",
                  block: "center",
                });
                setTimeout(() => {
                  isProgrammaticScroll.current = false;
                  setTimeout(() => {
                    scrollDetectionLock.current = false;
                  }, 200);
                }, 50);
              }
            }
            if (number !== lastNumberRef.current) {
              lastNumberRef.current = number;
              onNumberChange(number);
            }
          }
        }, 80); // Debounce scroll detection
      };
      let ticking = false;
      const throttledScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            handleScroll();
            ticking = false;
          });
          ticking = true;
        }
      };

      // Add window-level wheel event handling to enable scrolling anywhere on the page
      const handleWheel = e => {
        if (isShuffling || !allowScrollDetection || currentNumber === 0) {
          return;
        }
        if (isProgrammaticScroll.current || scrollDetectionLock.current) {
          return;
        }

        e.preventDefault(); // Prevent default page scroll

        // Translate wheel delta to container scroll
        const deltaY = e.deltaY;
        const currentScrollTop = container.scrollTop;
        const newScrollTop = currentScrollTop + deltaY;

        // Apply the scroll to the container
        container.scrollTop = newScrollTop;
      };

      // Add touch handling for mobile
      let touchStartY = 0;
      const handleTouchStart = e => {
        touchStartY = e.touches[0].clientY;
      };

      const handleTouchMove = e => {
        if (isShuffling || !allowScrollDetection || currentNumber === 0) {
          return;
        }
        if (isProgrammaticScroll.current || scrollDetectionLock.current) {
          return;
        }

        const touchY = e.touches[0].clientY;
        const deltaY = touchStartY - touchY;

        if (Math.abs(deltaY) > 5) {
          // Minimum threshold for touch movement
          e.preventDefault(); // Prevent default page scroll

          const currentScrollTop = container.scrollTop;
          const newScrollTop = currentScrollTop + deltaY;

          container.scrollTop = newScrollTop;
          touchStartY = touchY; // Update for continuous scrolling
        }
      };

      // Attach listeners
      container.addEventListener("scroll", throttledScroll);
      window.addEventListener("wheel", handleWheel, { passive: false });
      window.addEventListener("touchstart", handleTouchStart, {
        passive: true,
      });
      window.addEventListener("touchmove", handleTouchMove, { passive: false });

      const initialScrollTimer = setTimeout(() => {
        if (allowScrollDetection && currentNumber !== 0) {
          handleScroll();
        }
      }, 500);

      return () => {
        container.removeEventListener("scroll", throttledScroll);
        window.removeEventListener("wheel", handleWheel);
        window.removeEventListener("touchstart", handleTouchStart);
        window.removeEventListener("touchmove", handleTouchMove);
        clearTimeout(initialScrollTimer);
        if (debounceTimer) clearTimeout(debounceTimer);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      currentNumber,
      onNumberChange,
      isShuffling,
      allowScrollDetection,
      isNavigating,
    ]);

    // Shuffle function
    const handleShuffle = () => {
      if (isShuffling || isNavigating) return;

      setIsShuffling(true);
      setIsNavigating(true);

      // 1. Pick a completely random target from 1-42
      const targetNumber = Math.floor(Math.random() * 42) + 1;

      // 2. Time-based shuffle with visual feedback (3.5 seconds total)
      const totalDuration = 3500; // 3.5 seconds - optimal for anticipation without boredom
      const startTime = Date.now();
      let currentIndex = currentNumber;
      let intervalSpeed = 80; // Start speed (ms between changes)

      const shuffle = () => {
        const elapsed = Date.now() - startTime;
        const progress = elapsed / totalDuration;

        // 3. Gradually slow down using easing curve (ease-out)
        // Speed increases exponentially as we approach the end
        const easedProgress = 1 - Math.pow(1 - progress, 3); // Cubic ease-out
        intervalSpeed = 80 + easedProgress * 200; // 80ms to 280ms

        // 4. Random movement during first 80% of duration
        if (progress < 0.8) {
          // Random walk during the "spinning" phase
          const directions = [-1, 1];
          const direction = directions[Math.floor(Math.random() * 2)];
          currentIndex += direction;

          // Wrap around the range
          if (currentIndex < 1) currentIndex = 42;
          if (currentIndex > 42) currentIndex = 1;

          navigateToNumber(currentIndex, "auto");
          onNumberChange(currentIndex, true); // Play tick sound

          setTimeout(shuffle, intervalSpeed);
        }
        // 5. Final approach phase - move toward target
        else if (progress < 1.0) {
          // Direct movement toward target in final 20%
          if (currentIndex !== targetNumber) {
            // Choose shortest path to target
            const diff = targetNumber - currentIndex;
            const wrappedDiff =
              diff > 21 ? diff - 42 : diff < -21 ? diff + 42 : diff;

            if (wrappedDiff > 0) {
              currentIndex = currentIndex === 42 ? 1 : currentIndex + 1;
            } else if (wrappedDiff < 0) {
              currentIndex = currentIndex === 1 ? 42 : currentIndex - 1;
            }

            navigateToNumber(currentIndex, "auto");
            onNumberChange(currentIndex, true);

            setTimeout(shuffle, intervalSpeed);
          } else {
            // We've reached the target
            finalizeShuffle();
          }
        } else {
          // Time's up, go directly to target
          finalizeShuffle();
        }
      };

      const finalizeShuffle = () => {
        // Ensure we end up at the predetermined target
        navigateToNumber(targetNumber, "smooth");
        setTimeout(() => {
          setIsShuffling(false);
          setIsNavigating(false);
          lastNumberRef.current = targetNumber;
        }, 500); // Allow time for final smooth navigation
      };

      // Start the shuffle
      shuffle();
    };

    // Initialize array and position based on current number
    useEffect(() => {
      if (currentNumber > 0) {
        // This effect is no longer needed with the simplified approach
        // Just ensure scroll detection is enabled when we have a valid number
        if (!allowScrollDetection) {
          setTimeout(() => {
            setAllowScrollDetection(true);
          }, 100);
        }
      }
    }, [currentNumber, allowScrollDetection]);

    // Cleanup navigation timeout on unmount
    useEffect(() => {
      return () => {
        if (navigationTimeoutRef.current) {
          clearTimeout(navigationTimeoutRef.current);
        }
      };
    }, []);

    // In the return statement
    return (
      <>
        <ScrollerContainer ref={containerRef}>
          <NumberWrapper>
            {infiniteArray.map((number, index) => (
              <NumberItem
                key={`infinite-${number}-${index}`}
                data-number={number}
                data-position={index}
                active={
                  currentNumber === number &&
                  currentNumber !== 0 &&
                  !isShuffling
                } // No active numbers when currentNumber is 0 or shuffling
                onClick={() => {
                  if (!isShuffling && !isNavigating) {
                    navigateToNumber(number);
                  }
                }}
                whileTap={{ scale: 0.95 }}
              >
                <NoText
                  active={
                    currentNumber === number &&
                    currentNumber !== 0 &&
                    !isShuffling
                  }
                >
                  No.
                </NoText>
                <NumberContent>
                  {number.toString().padStart(2, "0")}
                  <CircleWrapper
                    active={
                      currentNumber === number &&
                      currentNumber !== 0 &&
                      !isShuffling
                    }
                  >
                    <AnimatedCircle
                      width="353"
                      height="286"
                      viewBox="0 0 353 286"
                    >
                      <CirclePath
                        d="M239.952 4C182.605 4 131.504 18.3871 84.8023 53.0177C33.159 91.3124 -0.700826 148.786 4.53189 214.256C7.71636 254.099 40.0999 275.98 77.5586 280.144C128.8 285.841 180.199 278.722 226.055 254.628C258.553 237.553 285.711 210.954 308.094 182.277C325.319 160.208 340.951 139.395 345.744 111.268C347.905 98.5885 349.632 85.2015 348.777 72.3227C347.672 55.6921 340.438 46.2233 326.624 37.1541C281.319 7.4106 231.832 3.66359 179.307 8.53246"
                        initial={{
                          strokeDasharray: "1200 1200",
                          strokeDashoffset: 1200,
                        }}
                        animate={
                          currentNumber === number &&
                          currentNumber !== 0 &&
                          !isShuffling
                            ? {
                                strokeDashoffset: 0,
                              }
                            : {
                                strokeDashoffset: 1200,
                              }
                        }
                        transition={{
                          duration: 1,
                          ease: "easeInOut",
                        }}
                      />
                    </AnimatedCircle>
                  </CircleWrapper>
                </NumberContent>
              </NumberItem>
            ))}
          </NumberWrapper>
        </ScrollerContainer>
        <ContentOverlay />
        <ButtonContainer>
          <QuestionButton onClick={() => setIsHitchhikerDialogOpen(true)}>
            ?
          </QuestionButton>
          <QRButton onClick={onOpenQRDialog}>
            <img src={qrCodeImage} alt="QR Code" />
          </QRButton>
          <ShuffleButton onClick={handleShuffle} disabled={isShuffling}>
            SHUFFLE
          </ShuffleButton>
        </ButtonContainer>
        <HitchhikerDialog
          isOpen={isHitchhikerDialogOpen}
          onClose={() => setIsHitchhikerDialogOpen(false)}
        />
      </>
    );
  }
);

export default NumberScroller;
